/**
 * useAffiliateConfig Hook
 * 
 * Custom hook to fetch and cache affiliate commission configuration from the backend.
 * This ensures all affiliate-related displays use the same dynamic rate from the database.
 * 
 * Returns:
 *   - commissionRate: Current commission percentage (e.g., 20.0)
 *   - commissionPerSale: Calculated commission amount per sale (e.g., 5.80)
 *   - premiumPrice: Premium subscription price (e.g., 29.0)
 *   - currency: Currency code (e.g., "EUR")
 *   - loading: Boolean indicating if data is being fetched
 *   - error: Error message if fetch fails
 *   - refetch: Function to manually refetch the configuration
 * 
 * Usage:
 *   const { commissionRate, commissionPerSale, loading } = useAffiliateConfig();
 */

import { useState, useEffect } from 'react';

import { getBackendUrl, getApiUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();
const CACHE_KEY = 'affiliate_config_cache';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache

export const useAffiliateConfig = () => {
  const [config, setConfig] = useState({
    commissionRate: 20.0, // Default fallback
    commissionPerSale: 5.80, // Default fallback
    premiumPrice: 29.0,
    currency: 'EUR'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      // Check cache first
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const now = Date.now();
        
        // Use cache if less than CACHE_DURATION old
        if (now - timestamp < CACHE_DURATION) {
          setConfig(data);
          setLoading(false);
          return;
        }
      }

      // ✅ CRITICAL FIX: Use native fetch to avoid rrweb interception and Response clone errors
      const nativeFetch = window.__nativeFetch__ || window.fetch.bind(window);
      const response = await nativeFetch(`${BACKEND_URL}/api/affiliate/config/public`);
      
      // ✅ Handle 500 errors gracefully - use defaults without throwing
      if (!response.ok) {
        console.debug('[useAffiliateConfig] API returned', response.status, '- using defaults');
        setConfig({
          commissionRate: 20.0,
          commissionPerSale: 5.80,
          premiumPrice: 29.0,
          currency: 'EUR'
        });
        setLoading(false);
        return;
      }

      // ✅ Read response body once to avoid clone issues
      const text = await response.text();
      const data = text ? JSON.parse(text) : {};
      
      const formattedConfig = {
        commissionRate: data.commission_rate || 20.0,
        commissionPerSale: data.commission_per_sale || 5.80,
        premiumPrice: data.premium_price || 29.0,
        currency: data.currency || 'EUR'
      };

      // Update state
      setConfig(formattedConfig);

      // Cache the result
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        data: formattedConfig,
        timestamp: Date.now()
      }));

    } catch (err) {
      // ✅ Silently use fallback values on any error (network, parsing, etc.)
      console.debug('[useAffiliateConfig] Error, using defaults:', err.message);
      setError(err.message);
      
      // Use fallback values on error
      setConfig({
        commissionRate: 20.0,
        commissionPerSale: 5.80,
        premiumPrice: 29.0,
        currency: 'EUR'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  return {
    ...config,
    loading,
    error,
    refetch: fetchConfig
  };
};

// Helper function to clear cache (useful when admin updates the rate)
export const clearAffiliateConfigCache = () => {
  localStorage.removeItem(CACHE_KEY);
};

export default useAffiliateConfig;
